import type { ComponentType } from 'react';
import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import FileMarkdownIcon from '@atlaskit/icon-lab/core/file-markdown';
import NotepadIcon from '@atlaskit/icon-lab/core/notepad';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import DatabaseIcon from '@atlaskit/icon/core/database';
import AiGenerativeSlidesIcon from '@atlaskit/icon-lab/core/ai-generative-slides';
import FolderIcon from '@atlaskit/icon/core/folder-closed';
import PlusSquareIcon from '@atlaskit/icon/core/plus-square';
import type { NewCoreIconProps } from '@atlaskit/icon/types';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon.ts';
import { ManagedFolderClosedIcon } from '@confluence/smart-folder/entry-points/ManagedFolderClosedIcon.ts';
import { isSmartFolder } from '@confluence/smart-folder/entry-points/isSmartFolder.ts';

type ContentIconProps = {
	type: string;
	emoji?: string;
	color?: NewCoreIconProps['color'];
	subType?: string | null;
	height?: number;
	isMarkdownMode?: boolean;
};

export const ContentIcon = ({
	type,
	emoji,
	color,
	subType,
	height = 16,
	isMarkdownMode = false,
}: ContentIconProps): React.JSX.Element | null => {
	const isLivePage = (type === 'page' && subType === 'live') || type === 'liveDoc';

	// embed favicon/default content type icon will also be handled by the emoji component
	if (isValidEmojiOrFavicon(emoji, type)) {
		return (
			<LazyEmojiComponentLoader
				emoji={emoji || ''}
				contentType={type}
				context="contentMenu"
				height={height}
				wrapper={({ children }: { children?: React.ReactNode }) => (
					<Box xcss={emojiWrapperStyles}>{children}</Box>
				)}
			/>
		);
	}

	if (isSmartFolder({ type, subType })) {
		return (
			<ManagedFolderClosedIcon
				label=""
				size="small"
				color={color}
				testId="icons.content-icon.label.managed-folder"
			/>
		);
	}

	const actualType = isLivePage && isMarkdownMode ? 'markdown' : isLivePage ? 'liveDoc' : type;
	const definition = iconDefinitions[actualType];

	if (!definition) {
		return null;
	}

	const { icon: Icon, label } = definition;
	return <Icon label="" color={color} testId={label.id} />;
};

const emojiWrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	maxWidth: '16px',
	width: '16px',
	height: '16px',
	font: 'font.body.large',
	overflow: 'hidden',
});

const i18n = defineMessages({
	liveDoc: {
		id: 'icons.content-icon.label.live-doc',
		defaultMessage: 'Live Doc',
		description: "Label for the 'live doc' content type icon in the content menu",
	},
	page: {
		id: 'icons.content-icon.label.page',
		defaultMessage: 'Page',
		description: "Label for the 'page' content type icon in the content menu",
	},
	blogpost: {
		id: 'icons.content-icon.label.blogpost',
		defaultMessage: 'Blog',
		description: "Label for the 'blog' content type icon in the content menu",
	},
	whiteboard: {
		id: 'icons.content-icon.label.whiteboard',
		defaultMessage: 'Whiteboard',
		description: "Label for the 'whiteboard' content type icon in the content menu",
	},
	database: {
		id: 'icons.content-icon.label.database',
		defaultMessage: 'Database',
		description: "Label for the 'database' content type icon in the content menu",
	},
	slide: {
		id: 'icons.content-icon.label.slide',
		defaultMessage: 'Slide',
		description: "Label for the 'slide' content type icon in the content menu",
	},
	folder: {
		id: 'icons.content-icon.label.folder',
		defaultMessage: 'Folder',
		description: "Label for the 'folder' content type icon in the content menu",
	},
	space: {
		id: 'icons.content-icon.label.space',
		defaultMessage: 'Space',
		description: "Label for the 'space' icon in the content menu",
	},
	note: {
		id: 'icons.content-icon.label.note',
		defaultMessage: 'Note',
		description: "Label for the 'note' icon in the content menu",
	},
	markdown: {
		id: 'icons.content-icon.label.markdown',
		defaultMessage: 'Markdown',
		description: "Label for the 'markdown' icon in the content menu",
	},
});

const iconDefinitions: Record<
	string,
	{
		icon: ComponentType<NewCoreIconProps>;
		label: MessageDescriptor;
	}
> = {
	liveDoc: {
		icon: PageLiveDocIcon,
		label: i18n.liveDoc,
	},
	page: {
		icon: PageIcon,
		label: i18n.page,
	},
	blogpost: {
		icon: QuotationMarkIcon,
		label: i18n.blogpost,
	},
	whiteboard: {
		icon: WhiteboardIcon,
		label: i18n.whiteboard,
	},
	database: {
		icon: DatabaseIcon,
		label: i18n.database,
	},
	slide: {
		icon: AiGenerativeSlidesIcon,
		label: i18n.slide,
	},
	folder: {
		icon: FolderIcon,
		label: i18n.folder,
	},
	space: {
		icon: PlusSquareIcon,
		label: i18n.space,
	},
	note: {
		icon: NotepadIcon,
		label: i18n.note,
	},
	markdown: {
		icon: FileMarkdownIcon,
		label: i18n.markdown,
	},
};
